import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { rxResource } from '@angular/core/rxjs-interop';
import { environment } from '../../../../../environments/environment';
import { User } from '../../interfaces/user.interface';
import { AuthResponse } from '../../interfaces/auth-response.interface';
import { Router } from '@angular/router';
import { ImpersonateResponse, ImpersonationState } from '@admin/interfaces/impersonate.interface';

type AuthStatus = 'checking' | 'authenticated' | 'not-authenticated';
const baseUrl = environment.baseUrl;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _authStatus = signal<AuthStatus>('checking');
  private _user = signal<User | null>(null);
  private _token = signal<string | null>(localStorage.getItem('token'));
  private _refreshToken = signal<string | null>(localStorage.getItem('refreshToken'));
  private _impersonationState = signal<ImpersonationState>(this.readStoredImpersonation());

  /**
   * Petición de refresh en vuelo (singleflight): todas las llamadas
   * concurrentes (interceptor, guards, renovación silenciosa) comparten la
   * misma petición para no rotar el refresh token dos veces y que el backend
   * lo interprete como reuso/robo de sesión.
   */
  private refreshRequest: Observable<boolean | null> | null = null;

  private http = inject(HttpClient);
  private router = inject(Router);

  authStatus = computed<AuthStatus>(() => {
    if (this._authStatus() === 'checking') return 'checking';

    if (this._user()) {
      return 'authenticated';
    }

    return 'not-authenticated';
  });

  user = computed(() => this._user());
  token = computed(this._token);
  impersonationState = computed(() => this._impersonationState());

  login(email: string, password: string): Observable<boolean | null> {
    return this.http
      .post<AuthResponse>(`${baseUrl}/auth/login`, {
        email: email,
        password: password,
      })
      .pipe(
        map((resp) => this.handleAuthSuccess(resp)),
        catchError((error: any) => this.handleAuthError(error)),
      );
  }

  loginWithGoogle() {
    window.location.href = `${baseUrl}/auth/google`;
  }

  exchangeGoogleCode(code: string): Observable<boolean | null> {
    return this.http.post<AuthResponse>(`${baseUrl}/auth/exchange-google-code`, { code }).pipe(
      map((resp) => this.handleAuthSuccess(resp)),
      catchError((error: any) => this.handleAuthError(error)),
    );
  }

  register(email: string, password: string, fullname: string): Observable<boolean> {
    return this.http
      .post<AuthResponse>(`${baseUrl}/auth/register`, {
        email: email,
        password: password,
        fullName: fullname,
      })
      .pipe(
        map(() => true),
        catchError(() => of(false)),
      );
  }

  activateAccount(token: string) {
    return this.http.post<any>(`${baseUrl}/auth/activate-account`, { token });
  }

  resendActivationEmail(email: string) {
    return this.http.post<any>(`${baseUrl}/auth/resend-activation`, { email });
  }

  checkStatus(): Observable<boolean | null> {
    const token = localStorage.getItem('token');
    if (!token) {
      // Solo actualiza estado sin redirigir
      this._user.set(null);
      this._token.set(null);
      this._refreshToken.set(null);
      this._authStatus.set('not-authenticated');
      return of(false);
    }

    return this.http.get<AuthResponse | ImpersonateResponse>(`${baseUrl}/auth/check-status`).pipe(
      map((resp) => {
        // Si el token pertenece a una sesión de impersonación, se restaura el
        // estado sin tocar el refresh token del admin.
        if ('isImpersonating' in resp && resp.isImpersonating) {
          this.applyImpersonation(resp);
          return true;
        }
        return this.handleAuthSuccess(resp);
      }),
      catchError((error: any) => {
        // Similar: solo limpia el estado, sin redirigir
        this._user.set(null);
        this._token.set(null);
        this._refreshToken.set(null);
        this._authStatus.set('not-authenticated');
        return of(false);
      }),
    );
  }

  logout() {
    // Guarda la ruta actual para volver tras el próximo login y no perder contexto
    const actual = this.router.url;
    if (actual && !actual.startsWith('/auth')) {
      sessionStorage.setItem('postLoginRedirect', actual);
    }

    // Intentar invalidar el refresh token en el servidor
    this.http.post(`${baseUrl}/auth/logout`, {}).subscribe({
      error: () => {
        // Si falla, ignoramos — la limpieza local es suficiente
      },
    });

    this._user.set(null);
    this._token.set(null);
    this._refreshToken.set(null);
    this._authStatus.set('not-authenticated');

    this.clearImpersonation();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    this.router.navigateByUrl('/auth/login');
  }

  updateProfile(userLike: Partial<User>): Observable<any> {
    return this.http.patch<AuthResponse>(`${baseUrl}/auth/update-profile`, userLike).pipe(
      map((resp) => this.handleAuthSuccess(resp)),
      catchError((error: any) => this.handleAuthError(error)),
    );
  }

  updatePassword(userLike: any): Observable<any> {
    return this.http.patch(`${baseUrl}/auth/update-password`, userLike).pipe(
      tap(() => {
        this.logout();
      }),
    );
  }

  refreshToken(): Observable<boolean | null> {
    // Si ya hay una renovación en curso, los nuevos solicitantes esperan a la misma
    if (this.refreshRequest) return this.refreshRequest;

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return of(false);

    const request = this.http.post<AuthResponse>(`${baseUrl}/auth/refresh-token`, { refreshToken }).pipe(
      map((resp) => {
        // Si expira el access token de impersonación, el refresh restaura la
        // sesión del admin: se limpia el estado de impersonación.
        this.clearImpersonation();
        return this.handleAuthSuccess(resp);
      }),
      // No se cierra la sesión aquí: cada llamante decide (el interceptor
      // expulsa, el servicio de sesión avisa primero con cuenta atrás).
      catchError(() => of(false)),
      finalize(() => {
        this.refreshRequest = null;
      }),
      shareReplay(1),
    );
    this.refreshRequest = request;
    return request;
  }

  /** Ruta guardada al expulsar (para volver tras el login). La consume una sola vez. */
  consumePostLoginRedirect(): string | null {
    const destino = sessionStorage.getItem('postLoginRedirect');
    sessionStorage.removeItem('postLoginRedirect');
    if (destino && destino.startsWith('/') && !destino.startsWith('/auth')) return destino;
    return null;
  }

  /** Caducidad del access token en ms (epoch) o null si no hay token válido. */
  getAccessTokenExpiry(): number | null {
    const token = this._token() ?? localStorage.getItem('token');
    if (!token) return null;
    const partes = token.split('.');
    if (partes.length !== 3) return null;
    try {
      const base64 = partes[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64)) as { exp?: unknown };
      if (typeof payload.exp !== 'number') return null;
      return payload.exp * 1000;
    } catch {
      // Token corrupto o no decodificable: se trata como sin caducidad conocida
      return null;
    }
  }

  /** Sincroniza las señales con localStorage (multipestaña: otra pestaña renovó o cerró sesión). */
  reloadTokensFromStorage(): void {
    const token = localStorage.getItem('token');
    this._token.set(token);
    this._refreshToken.set(localStorage.getItem('refreshToken'));
    if (!token) {
      this._user.set(null);
      if (this._authStatus() !== 'checking') this._authStatus.set('not-authenticated');
    }
  }

  // ─── Impersonación (admin accede como otro usuario) ──────────────────────

  /** Inicia una sesión de impersonación con el usuario indicado. */
  startImpersonation(userId: string): Observable<boolean> {
    return this.http.post<ImpersonateResponse>(`${baseUrl}/admin/impersonate/${userId}`, {}).pipe(
      map((resp) => {
        this.applyImpersonation(resp);
        // Recarga completa: el dashboard del coach y el del admin comparten la
        // ruta '/', así que sin recargar el componente no se reconstruiría.
        window.location.reload();
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  /** Termina la sesión de impersonación y restaura la sesión del admin. */
  stopImpersonation(): Observable<boolean> {
    return this.http.post<AuthResponse>(`${baseUrl}/admin/stop-impersonate`, {}).pipe(
      map((resp) => {
        this.clearImpersonation();
        this.handleAuthSuccess(resp);
        // Recarga completa para reconstruir todos los componentes con la sesión
        // del admin (el dashboard no se re-renderiza si la ruta no cambia).
        window.location.reload();
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  /** Aplica una respuesta de impersonación sin tocar el refresh token del admin. */
  private applyImpersonation(resp: ImpersonateResponse): void {
    this._user.set(resp.user);
    this._authStatus.set('authenticated');
    this._token.set(resp.token);
    localStorage.setItem('token', resp.token);

    const state: ImpersonationState = {
      active: true,
      originalAdmin: resp.originalAdmin,
      targetUser: resp.user,
    };
    this._impersonationState.set(state);
    localStorage.setItem('impersonation', JSON.stringify(state));
  }

  private clearImpersonation(): void {
    this._impersonationState.set({ active: false });
    localStorage.removeItem('impersonation');
  }

  /** Restaura el estado de impersonación guardado (tras recargar la página). */
  private readStoredImpersonation(): ImpersonationState {
    try {
      const raw = localStorage.getItem('impersonation');
      if (raw) {
        const state = JSON.parse(raw) as ImpersonationState;
        if (state && state.active) return state;
      }
    } catch {
      // JSON corrupto: se ignora y se parte de un estado limpio
    }
    return { active: false };
  }

  private handleAuthSuccess({ token, refreshToken, user }: { user: User; token: string; refreshToken?: string }) {
    if (!user.isActive) return null;
    this._user.set(user);
    this._authStatus.set('authenticated');
    this._token.set(token);
    if (refreshToken) {
      this._refreshToken.set(refreshToken);
      localStorage.setItem('refreshToken', refreshToken);
    }
    localStorage.setItem('token', token);

    return true;
  }

  private handleAuthError(error: any) {
    this.logout();
    return of(false);
  }
}
